import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, inr, STATUS_LABELS } from "../../lib/api";
import { Download, MessageCircle, Star } from "lucide-react";
import { toast } from "sonner";
import { ProjectStagesCard, ApprovalsCustomerCard, ProjectFilesCustomerCard, RequirementsSummaryCard, InvoiceCard } from "./ProjectSections";

export default function OrderDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [rating, setRating] = useState(5);
  const [reviewText, setReviewText] = useState("");
  const [revOpen, setRevOpen] = useState(false);
  const [revNotes, setRevNotes] = useState("");
  const [revSubmitting, setRevSubmitting] = useState(false);
  const [revisions, setRevisions] = useState({ items: [], revisions_limit: 0, revisions_used: 0 });
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const load = () => {
    api.get(`/orders/${id}`).then(r => setData(r.data));
    api.get(`/orders/${id}/revisions`).then(r => setRevisions(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [id]);

  if (!data) return <div className="bg-white rounded-2xl p-12 text-center text-gray-500">Loading…</div>;
  const { order, deliverables, designer } = data;
  const revLimit = revisions.revisions_limit ?? order.revisions_limit ?? 2;
  const revUsed = revisions.revisions_used ?? order.revisions_used ?? 0;
  const revRemaining = revLimit === -1 ? "Unlimited" : Math.max(0, revLimit - revUsed);

  const submitReview = async () => {
    try {
      await api.post("/reviews/submit", { order_id: order.id, rating, review_text: reviewText });
      toast.success("Thanks for your review!");
      setReviewOpen(false);
    } catch (e) { toast.error("Couldn't submit review"); }
  };

  const submitRevision = async () => {
    if (revNotes.trim().length < 5) { toast.error("Please write at least a few words about the changes."); return; }
    setRevSubmitting(true);
    try {
      const { data: res } = await api.post(`/orders/${order.id}/request-revision`, { notes: revNotes.trim() });
      toast.success(`Revision sent to designer · ${res.revisions_remaining === -1 ? "Unlimited" : res.revisions_remaining} remaining`);
      setRevOpen(false);
      setRevNotes("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't submit revision");
    } finally { setRevSubmitting(false); }
  };

  return (
    <div className="space-y-5">
      <Link to="/dashboard" className="text-sm text-[#F7941D] hover:underline">← Back to orders</Link>

      <div className="bg-white rounded-2xl border border-[#1a3c5e]/10 p-6">
        <div className="flex flex-wrap justify-between items-start gap-3">
          <div>
            <h2 className="font-display text-2xl font-bold text-[#1a3c5e]">{order.service_name}</h2>
            <div className="text-xs text-gray-500 mt-1">Order #{order.id.slice(0,8)} · Placed {new Date(order.created_at).toLocaleDateString("en-IN")}</div>
          </div>
          <div className="text-right">
            <div className="font-display text-2xl font-bold text-[#F7941D]">{inr(order.amount)}</div>
            <div className="text-[11px] uppercase tracking-wide text-[#1a3c5e]/60 font-semibold">{STATUS_LABELS[order.status]}</div>
          </div>
        </div>

        {order.status === "requirements_pending" && (
          <div className="mt-5 p-4 rounded-xl bg-[#FEF3E2] border border-[#F7941D]/30">
            <div className="font-semibold text-[#1a3c5e]">Action required</div>
            <p className="text-sm text-gray-600 mt-1">Submit your project requirements to begin design work.</p>
            <Link to={`/dashboard/requirements/${order.id}`}>
              <button className="mt-3 px-5 py-2 bg-[#F7941D] hover:bg-[#e0861a] text-white font-semibold rounded-xl text-sm" data-testid="goto-requirements">Submit Requirements →</button>
            </Link>
          </div>
        )}

        <Link to="/dashboard/inbox" data-testid="message-us-link">
          <button className="mt-4 px-4 py-2 border border-[#1a3c5e]/20 text-[#1a3c5e] font-semibold rounded-xl text-sm flex items-center gap-2 hover:bg-[#FEF3E2]/40">
            <MessageCircle className="w-4 h-4 text-[#25D366]" /> Message us about this order
          </button>
        </Link>
      </div>

      {/* Progress — driven by the live project workflow, the single source of truth for stage */}
      <ProjectStagesCard projectId={order.id} />
      <RequirementsSummaryCard projectId={order.id} />
      <ApprovalsCustomerCard projectId={order.id} onChange={load} />
      <ProjectFilesCustomerCard projectId={order.id} />
      <InvoiceCard projectId={order.id} />

      {designer && (
        <div className="bg-white rounded-2xl border border-[#1a3c5e]/10 p-6" data-testid="assigned-designer-card">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#1a3c5e] to-[#F7941D] text-white font-bold text-xl flex items-center justify-center overflow-hidden flex-shrink-0">
              {designer.avatar ? <img src={designer.avatar} alt="" className="w-full h-full object-cover" /> : (designer.first_name?.[0] || "D")}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Your Designer</div>
                {designer.verified_badge && (
                  <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold flex items-center gap-1">
                    <svg viewBox="0 0 24 24" className="w-3 h-3 fill-current"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                    HDW Verified
                  </span>
                )}
              </div>
              <div className="font-display text-xl font-bold text-[#1a3c5e]">{designer.first_name}</div>
              <div className="text-sm text-gray-600">{designer.role}{designer.years_experience > 0 && ` · ${designer.years_experience}+ yrs experience`}</div>
              {designer.skills?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {designer.skills.slice(0, 5).map(s => <span key={s} className="px-2 py-0.5 bg-[#FEF3E2] text-[#F7941D] text-[10px] font-bold rounded">{s}</span>)}
                </div>
              )}
            </div>
            <div className="flex gap-4 text-center pt-1">
              <div>
                <div className="font-display text-xl font-extrabold text-[#1a3c5e]">★ {designer.avg_rating || "—"}</div>
                <div className="text-[10px] text-gray-500 uppercase tracking-wider">Rating</div>
              </div>
              <div>
                <div className="font-display text-xl font-extrabold text-[#1a3c5e]">{designer.total_completed || 0}</div>
                <div className="text-[10px] text-gray-500 uppercase tracking-wider">Done</div>
              </div>
              <div>
                <div className="font-display text-xl font-extrabold text-[#1a3c5e]">{designer.on_time_delivery_pct ?? 100}%</div>
                <div className="text-[10px] text-gray-500 uppercase tracking-wider">On-time</div>
              </div>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-100 text-xs text-gray-500 flex items-center gap-2">
            <MessageCircle className="w-3.5 h-3.5 text-[#25D366]" />
            All messages with your designer go through HouseDesignWala for your safety and quality.
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-[#1a3c5e]/10 p-6">
        <h3 className="font-display font-bold text-[#1a3c5e] mb-4">Deliverables</h3>
        {deliverables.length === 0 ? (
          <p className="text-sm text-gray-500">Files will appear here once your designer uploads.</p>
        ) : (
          <div className="space-y-2">
            {deliverables.map(d => (
              <div key={d.id} className="flex items-center justify-between p-3 rounded-xl border border-[#1a3c5e]/10 hover:bg-[#FEF3E2]/40">
                <div>
                  <div className="font-semibold text-[#1a3c5e] text-sm">{d.file_name}</div>
                  <div className="text-xs text-gray-500">v{d.version} · {(d.size / 1024 / 1024).toFixed(2)} MB · {new Date(d.uploaded_at).toLocaleDateString()}</div>
                </div>
                <a href={`${BACKEND_URL}/api/files/${d.id}`} download data-testid={`download-${d.id}`}>
                  <button className="px-4 py-2 bg-[#1a3c5e] hover:bg-[#122a42] text-white text-sm font-semibold rounded-xl flex items-center gap-2"><Download className="w-4 h-4" /> Download</button>
                </a>
              </div>
            ))}
          </div>
        )}
      </div>

      {(order.status === "delivered" || order.status === "in_progress" || revisions.items.length > 0) && (
        <div className="bg-white rounded-2xl border border-[#1a3c5e]/10 p-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h3 className="font-display font-bold text-[#1a3c5e]">Revision Requests</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {revLimit === -1 ? "Unlimited revisions" : `${revUsed} of ${revLimit} used · ${revRemaining} remaining`}
              </p>
            </div>
            {order.status === "delivered" && (revLimit === -1 || revUsed < revLimit) && (
              <button onClick={() => setRevOpen(true)} data-testid="request-revision-btn" className="px-4 py-2 bg-[#F7941D] hover:bg-[#e0861a] text-white text-sm font-semibold rounded-xl">
                Request Revision
              </button>
            )}
          </div>
          {revisions.items.length > 0 && (
            <div className="mt-4 space-y-2">
              {revisions.items.map(r => (
                <div key={r.id} className="p-3 rounded-xl bg-[#FEF3E2]/40 border border-[#F7941D]/20">
                  <div className="text-xs font-semibold text-[#F7941D] mb-1">Revision #{r.version} · {new Date(r.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</div>
                  <p className="text-sm text-gray-700">{r.notes}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {revOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => !revSubmitting && setRevOpen(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-lg font-bold text-[#1a3c5e]">Request Revision</h3>
            <p className="text-xs text-gray-500 mt-1 mb-3">Describe the changes you want. Designer will be notified.</p>
            <textarea
              rows={5}
              value={revNotes}
              onChange={(e) => setRevNotes(e.target.value)}
              placeholder="e.g. Increase living room by 2ft, move staircase to right side, add a separate puja room..."
              data-testid="revision-notes-input"
              className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#F7941D]"
            />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setRevOpen(false)} disabled={revSubmitting} className="px-4 py-2 border border-gray-300 text-gray-700 font-semibold rounded-xl">Cancel</button>
              <button onClick={submitRevision} disabled={revSubmitting} data-testid="revision-submit-btn" className="px-4 py-2 bg-[#F7941D] hover:bg-[#e0861a] disabled:opacity-50 text-white font-semibold rounded-xl">
                {revSubmitting ? "Sending…" : "Send to Designer"}
              </button>
            </div>
          </div>
        </div>
      )}

      {order.status === "delivered" || order.status === "completed" ? (
        <div className="bg-white rounded-2xl border border-[#1a3c5e]/10 p-6">
          <button onClick={() => setReviewOpen(!reviewOpen)} className="px-5 py-2.5 bg-[#F7941D] hover:bg-[#e0861a] text-white font-semibold rounded-xl flex items-center gap-2" data-testid="write-review-btn">
            <Star className="w-4 h-4" /> {reviewOpen ? "Cancel" : "Write a Review"}
          </button>
          {reviewOpen && (
            <div className="mt-4 space-y-3">
              <div className="flex gap-1">
                {[1,2,3,4,5].map(i => (
                  <button key={i} onClick={() => setRating(i)} data-testid={`rating-${i}`}>
                    <Star className={`w-7 h-7 ${i <= rating ? "fill-[#F7941D] text-[#F7941D]" : "text-gray-300"}`} />
                  </button>
                ))}
              </div>
              <textarea rows={4} value={reviewText} onChange={(e) => setReviewText(e.target.value)} data-testid="review-textarea" placeholder="Share your experience..." className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#F7941D]" />
              <button onClick={submitReview} disabled={!reviewText} data-testid="submit-review-btn" className="px-5 py-2.5 bg-[#1a3c5e] hover:bg-[#122a42] disabled:opacity-50 text-white font-semibold rounded-xl">Submit Review</button>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
